import { flatten, range } from 'lodash';
import { keyframes, css } from 'styled-components';

import { isiOS } from './config';
import { FULL_VH, animationFixStyle } from 'components/Common';

import iOSWarningIcon from 'inline-icons/ios-warning-icon.svg';
import androidWarningIcon from 'inline-icons/android-warning-icon.svg';

export const getTheme = () => (isiOS ? 'iOS' : 'material');

export const androidNavbarTopRowHeight = 56;
export const androidNavbarBottomRowHeight = 49;
export const androidNavbarHeight = ({ top = 0 }) => {
  if (top > androidNavbarTopRowHeight) {
    return `${androidNavbarBottomRowHeight}px`;
  }
  return `${androidNavbarTopRowHeight + androidNavbarBottomRowHeight - top}px`;
};

export const androidSlideDuration = 100;
export const androidTransitionDuration = 300;
export const iOSSlideDuration = 100;
export const iOSTransitionDuration = 400;

const iOSPageShadow = `
  position: absolute;
  right: 100%;
  top: 0;
  width: 16px;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 10%,
    rgba(0, 0, 0, 0.01) 50%,
    rgba(0, 0, 0, 0.2) 100%
  );
  z-index: -1;
  content: '';
  will-change: opacity;
  contain: strict;
`;

const iOSPulse = keyframes`
  50% { fill: #868686; }
  to { fill: rgba(134, 134, 134, .4) }
`;

const androidDashOffset = 187;
const androidLoadingDuration = '1.4s';

const androidDash = keyframes`
  0% {
    stroke-dashoffset: ${androidDashOffset};
  }
  50% {
    stroke-dashoffset: ${(androidDashOffset / 4).toFixed(2)};
    transform:rotate(135deg);
  }
  100% {
    stroke-dashoffset: ${androidDashOffset};
    transform:rotate(450deg);
  }
`;

const androidRotation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(270deg); }
`;

export const iOSNavbarHeightNumber = 44;
export const iOSNavbarHeight = `${iOSNavbarHeightNumber}px`;

export const iOSButtonRadius = '5px';
export const androidButtonRadius = '2px';

export const iOSEdgeDistance = 20;
export const iOSEdgeDistancePx = `${iOSEdgeDistance}px`;
export const androidEdgeDistance = 16;
export const androidEdgeDistancePx = `${androidEdgeDistance}px`;

export const borderColor = '#e7e7e7';
export const borderColorV2 = 'rgba(17,37,48,0.1)';
export const buttonRadius = isiOS ? iOSButtonRadius : androidButtonRadius;
export const fitbitLightGray = '#F6F6F6';
export const fitbitGray = '#707c83';
export const fitbitGreen = '#01b1ba';
export const fitbitDarkGreen = `#007A81`;
export const fitbitGreenV2 = '#007a81';
export const fitbitPink = '#ef426f';
export const fitbitMidnight = '#112530';
export const fitbitPremiumYellow = '#b7832f';
export const fitbitTextBlack = '#112530';
export const fitbitSectionTitleGray = '#707C83';
export const fitbitTextGray = '#707C83';
export const fitbitSectionBackgroundGray = '#E5E5E5';
export const fontBold = '"Proxima Nova Bold"';
export const fontLight = '"Proxima Nova Light"';
export const fontRegular = '"Proxima Nova Regular"';
export const fontSemibold = '"Proxima Nova Semibold"';
export const imageBorderRadius = isiOS ? '4px' : '5px';
export const warningIcon = isiOS ? iOSWarningIcon : androidWarningIcon;

// Modal
export const modalSectionBorderColor = '#edecf2';
export const modalToggleBgColor = 'none';
export const modalToggleBorderColor = '#aaa';
export const modalToggleCheckedBgColor = fitbitMidnight;
export const modalToggleCheckedColor = '#fff';

export const themes = {
  common: {
    borderColor,
    borderColorV2,
    buttonRadius,
    fitbitGray,
    fitbitGreen,
    fitbitGreenV2,
    fitbitDarkGreen,
    fitbitLightGray,
    fitbitPink,
    fitbitMidnight,
    fitbitPremiumYellow,
    fontBold,
    fontRegular,
    fontSemibold,
    imageBorderRadius,
    modalSectionBorderColor,
    modalToggleBgColor,
    modalToggleBorderColor,
    modalToggleCheckedBgColor,
    modalToggleCheckedColor,
    warningIcon
  },
  iOS: {
    global: `
      html, body {
        font: 18px ${fontRegular};
        width: 100%;
        height: 100%;
        overflow: hidden;
      }

      * {
        font-family: ${fontRegular};
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        -webkit-touch-callout: none;
        user-select: none;
      }

      input, textarea {
        -webkit-touch-callout: text;
        -webkit-user-select: text;
        user-select: text;
      }

      .slide-left, .slide-right, .forward, .back {
        background: #efeff4;

        .transition {
          height: 100vh;
          width: 100vw;
          overflow: hidden;
          position: relative;
          > div {
            background: transparent;
            position: absolute;
            height: 100%;
            left: 0;
            top: 0;
            width: 100%;
          }
        }

        .app-container, .gallery-container, .back-text, .nav-center, .nav-right {
          transition: all ${iOSTransitionDuration}ms;
          ${animationFixStyle}
        }

        .app-container, .gallery-container:before {
          transition: all ${iOSTransitionDuration}ms;
          ${animationFixStyle}
        }

        .back-text, .nav-center, .nav-right {
          display: block;
        }

        .navbar {
          overflow: hidden;
        }
      }

      .v2-top-bar {
        background: #FFFFFF !important;
      }

      .slide-left, .slide-right {
        .app-container, .gallery-container {
          transition: all ${iOSSlideDuration}ms;
          ${animationFixStyle}
        }
      }

      .transition {
        .navbar {
          transition: none;
        }

        .app-container {
          background: #f5f5f5;
        }
      }

      .forward {
        .slide-enter {
          z-index: 65536;

          .app-container, .gallery-container {
            transform: translate3d(100%, 0, 0);

            &:before {
              ${iOSPageShadow};
            }
          }

          .navbar {
            background-color: transparent;
          }

          .back-text, .nav-center, .nav-right {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
          }

          .back-text-v2, .permissions-title-v2 {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
            display: none;
          }
        }

        .slide-enter-active {
          .app-container, .gallery-container {
            transform: translate3d(0, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(0, 0, 0);
            display: none;
          }
        }

        .slide-leave {
          .back-text, .nav-center, .nav-right {
            opacity: 1;
          }

          .back-text-v2 {
            opacity: 0;
            display: none;
          }
        }

        .slide-leave-active {
          .app-container, .gallery-container {
            transform: translate3d(-20%, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 0;
            transform: translate3d(-20vw, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(-20vw, 0, 0);
            display: none;
          }
        }
      }

      .back {
        .slide-enter {
          .app-container, .gallery-container {
            transform: translate3d(-20%, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 0;
            transform: translate3d(-20vw, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(-20vw, 0, 0);
            display: none;
          }
        }

        .slide-enter-active {
          .app-container, .gallery-container {
            transform: translate3d(0, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(0, 0, 0);
            display: none;
          }
        }

        .slide-leave {
          z-index: 65536;

          .app-container, .gallery-container {
            &:before {
              ${iOSPageShadow};
            }
          }

          .navbar {
            background-color: transparent;
          }

          .back-text, .nav-center, .nav-right {
            opacity: 1;
            transform: translate3d(30vw, 0, 0);
          }

          .back-text-v2, .permissions-title-v2 {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
            display: none;
          }
        }

        .slide-leave-active {
          .app-container, .gallery-container {
            transform: translate3d(100%, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
            display: none;
          }
        }
      }

      .slide-left {
        .slide-enter {
          .app-container, .gallery-container {
            transform: translate3d(100%, 0, 0);
          }
        }

        .slide-enter-active, .slide-leave {
          .app-container, .gallery-container {
            transform: translate3d(0, 0, 0);
          }
        }

        .slide-leave-active {
          .app-container, .gallery-container {
            transform: translate3d(-100%, 0, 0);
          }
        }
      }

      .slide-right {
        .slide-enter {
          .app-container, .gallery-container {
            transform: translate3d(-100%, 0, 0);
          }
        }

        .slide-enter-active, .slide-leave {
          .app-container, .gallery-container {
            transform: translate3d(0, 0, 0);
          }
        }

        .slide-leave-active {
          .app-container, .gallery-container {
            transform: translate3d(100%, 0, 0);
          }
        }
      }

    `,
    edgeDistanceRaw: iOSEdgeDistance,
    edgeDistance: iOSEdgeDistancePx,
    backgroundColor: '#f5f5f5',
    marginTop: iOSNavbarHeight,
    AppListBorder: `1px solid ${borderColor}`,
    AppList: ({ borderTop }) => `
      background-color: white;

      ${borderTop ? `border-top: 1px solid ${borderColor};` : ''}
    `,
    AppDigestIcon: `
      height: 52px;
      width: 52px;
    `,
    AppMainContent: `
      border-bottom: 1px solid ${borderColor};
      border-top: 1px solid ${borderColor};
      > a {
        display: block;
      }
      a > div, > div {
        border-top: 1px solid ${borderColor};
        margin: 0;
        padding-left: 0;
      }
      > div:first-of-type {
        border-top: none;
      }
    `,
    Button: `
      border-radius: ${buttonRadius};
      width: 24vw;
    `,
    LoadingIcon: css`
      height: 27px;
      width: 27px;
      fill: #868686;

      path {
        ${flatten(
          range(12).map(
            (value) =>
              css`
                &:nth-of-type(${value + 1}) {
                  animation: ${iOSPulse} 1s ${-0.083 * value}s infinite linear;
                }
              `
          )
        )}
      }
    `,
    Modal: `
      margin-left: -40.67vw;
      width: 81.33vw;
      border-radius: 13px;
    `,
    ModalButtonGroup: `
      border-radius: 0 0 13px 13px;
    `,
    NavbarHeight: iOSNavbarHeight,
    Page: `
      -webkit-overflow-scrolling: touch;
      -webkit-backface-visibility: hidden;
      -webkit-transform-style: preserve-3d;
    `,
    PermissionContainer: `
      padding: 20px 0;
      max-height: ${0.9 * FULL_VH - 150}px;
    `,
    PermissionContent: '',
    PermissionDescription: `
      margin: -10px 0;
    `,
    PermissionList: `
      margin: -10px 0;
    `,
    PermissionLinkWrapper: `
      display: inline-block;
      width: 95%;
    `,
    PermissionWarningIcon: `
      float: right;
      width: 22px;
      height: 20px;
      margin-right: 5px;
    `,
    Swiper: `
      background-color: transparent;
      margin: 0 auto 40px;
      padding-top: 40px;
      img {
        border-radius: ${imageBorderRadius};
      }
    `,
    SwiperV2: `
      background-color: transparent;
      margin: 0 auto;
      padding-top: 40px;
      img {
        border-radius: ${imageBorderRadius};
      }
    `,
    SwiperSlider: `
      background-color: transparent;
    `
  },
  material: {
    global: `
      html, body {
        font-size: 18px ${fontRegular};
        width: 100%;
        height: 100%;
        overflow: hidden;
        background-color: #FFFFFF;
      }

      .transition {
        position: relative;
        height: 100vh;
        width: 100vw;
        overflow: hidden;
      }

      .transition > div {
        background: white;
        position: absolute;
        height: 100%;
        left: 0;
        top: 0;
        transition: all ${androidTransitionDuration}ms;
        width: 100%;
      }

      .slide-left, .slide-right {
        background: white;

        .transition > div {
          background: transparent;
        }

        .gallery-container {
          transition: all ${androidSlideDuration}ms;
        }
      }

      .forward {
        .slide-enter {
          opacity: 0;
          transform: translate3d(0, 56px, 0);
        }

        .slide-enter-active {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        .slide-leave {
          opacity: 1;
          z-index: -1;
        }
      }

      .back {
        .slide-enter {
          transform: translate3d(0, 0, 0);
          z-index: -1;
        }

        .slide-leave {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        .slide-leave-active {
          opacity: 0;
          transform: translate3d(0, 56px, 0);
        }
      }

      .slide-left {
        .slide-enter .gallery-container {
          transform: translate3d(100%, 0, 0);
        }

        .slide-enter-active .gallery-container {
          transform: translate3d(0, 0, 0);
        }

        .slide-leave .nav-bar {
          display: none;
        }

        .slide-leave .gallery-container {
          transform: translate3d(0, 0, 0);
        }

        .slide-leave-active .gallery-container {
          transform: translate3d(-100%, 0, 0);
        }
      }

      .slide-right {
        .slide-enter .gallery-container {
          transform: translate3d(-100%, 0, 0);
        }

        .slide-enter-active .gallery-container {
          transform: translate3d(0, 0, 0);
        }

        .slide-leave .nav-bar {
          display: none;
        }

        .slide-leave .gallery-container {
          transform: translate3d(0, 0, 0);
        }

        .slide-leave-active .gallery-container {
          transform: translate3d(100%, 0, 0);
        }
      }

      * {
        font-family: ${fontRegular};
        user-select: none;
      }
    `,
    edgeDistanceRaw: androidEdgeDistance,
    edgeDistance: androidEdgeDistancePx,
    backgroundColor: 'white',
    marginTop: androidNavbarHeight,
    AppList: ({ borderTop }) => {
      const rules = {
        'background-color': 'white'
      };
      if (borderTop) {
        rules['border-top'] = `1px solid ${borderColor}`;
      }
      return rules;
    },
    AppMainContent: `
      > div, a {
        border-bottom: 1px solid ${borderColor};
      },
      > div:first-of-type {
        border: none;
      }
      > a {
        display: block;
      }
    `,
    AppDigestIcon: `
      height: 48px;
      width: 48px;
    `,
    Button: `
      border-radius: ${buttonRadius};
      padding: 5px 0;
      width: 116px;
    `,
    LoadingIcon: css`
      height: 40px;
      width: 40px;
      animation: ${androidRotation} ${androidLoadingDuration} linear infinite;

      circle {
        stroke: #00b0b9;
        stroke-dasharray: ${androidDashOffset};
        stroke-dashoffset: 0;
        transform-origin: center;
        animation: ${androidDash} ${androidLoadingDuration} ease-in-out infinite;
      }
    `,
    Modal: `
      box-shadow: 0 19px 38px rgba(0, 0, 0, .3), 0 15px 12px rgba(0, 0, 0, .22);
      margin-left: -43.35vw;
      width: 86.7vw;
      border-radius: 5px;
    `,
    ModalButtonGroup: '',
    NavbarHeight: androidNavbarHeight,
    PermissionContainer: '',
    PermissionContent: '',
    PermissionDescription: '',
    PermissionList: '',
    PermissionLinkWrapper: '',
    PermissionWarningIcon: `
      float: right;
      width: 22px;
      height: 20px;
    `,
    Swiper: `
      background-color: white;
      margin-bottom: 42px;
      img {
        border-radius: ${imageBorderRadius};
      }
    `,
    SwiperV2: `
      background-color: white;
      margin-bottom: 10px;
      img {
        border-radius: ${imageBorderRadius};
      }
    `,
    SwiperSlider: `
      background-color: white;
    `
  }
};

export default {
  ...themes.common,
  ...themes[getTheme()]
};
